import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { Routes } from '@common/constants';
import { useAppContext } from '@common/contexts/AppContext';
import { ReduxPageType } from '@common-types/ReduxPage';
import { GOLF_DEFAULT_COURSE_IMAGE_URL } from '@features/golf/assets';
import { useLabels } from '@features/Menu/useLabels';
import { useModuleStatuses, usePropertyModules } from '@features/properties';
import { useRatePageSettings } from '@features/rates';
import { useRoomPageSettings } from '@features/rooms';
import {
  useGetSpaClassesPage,
  useGetSpaDaysPage,
  useGetSpaFacilitiesPage,
  useGetSpaStaysPage,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import type { Property } from 'shared-types/Property';
import type { Product } from 'shared-types/Product';
import type { CategoryItem } from 'voyager';
import { entersSearchFlow, getCategoryListRoute } from '../SearchFlow';
import { useFeaturedModuleVisibility } from '../useFeaturedModuleVisibility';

function tileImage(hero?: string | null, fallback?: string | null): string | null {
  return hero ?? fallback ?? null;
}

function categoryRoute(
  id: string
): { pathname: string; query?: Record<string, string> } | undefined {
  if (entersSearchFlow(id)) {
    return { pathname: Routes.SEARCH, query: { category: id, step: 'when' } };
  }
  const pathname = getCategoryListRoute(id);
  return pathname ? { pathname } : undefined;
}

function categoryHref(id: string): string | undefined {
  const route = categoryRoute(id);
  if (!route) return undefined;
  return route.query ? `${route.pathname}?${new URLSearchParams(route.query)}` : route.pathname;
}

function hasItems(page?: { hasListingItems?: boolean } | null): boolean {
  return page?.hasListingItems ?? true;
}

interface UseHomeCategoriesArgs {
  property: Pick<
    Property,
    'id' | 'name' | 'isTablesIntegrated' | 'isRetailEnabled' | 'modulesExcludingDisabled'
  >;
  language: string;
  retailListLoaded?: boolean;
}

interface UseHomeCategoriesResult {
  categories: CategoryItem[];
  selectCategory: (id: string) => void;
}

export function useHomeCategories({
  property,
  language,
  retailListLoaded = false,
}: UseHomeCategoriesArgs): UseHomeCategoriesResult {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const { siteId } = useAppContext();

  const { data: modules } = usePropertyModules(siteId);
  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } =
    useFeaturedModuleVisibility();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const isSpaEnabled = modules?.spa?.enabled ?? false;
  const isSpaStaysVisible =
    property?.modulesExcludingDisabled?.spa?.features?.stays?.enabled ?? false;
  const isClassesVisible = isSpaEnabled && (modules?.spa?.features?.classes?.enabled ?? false);
  const isFacilitiesVisible =
    isSpaEnabled && (modules?.spa?.features?.facilities?.enabled ?? false);
  const isRetailVisible = property?.isRetailEnabled;
  // GolfGate needs both flags; a tile without them would lead to a blank page.
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const isGolfVisible = isEcom2 && (property?.modulesExcludingDisabled?.golf?.enabled ?? false);

  const { content: roomsPage } = useRoomPageSettings(language, property?.name);
  const { content: ratesPage } = useRatePageSettings(language, property?.name);
  const { data: spaDaysPage } = useGetSpaDaysPage(property?.id, language);
  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property?.id, language);
  const { data: spaClassesPage } = useGetSpaClassesPage(property?.id, language);
  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property?.id, language);
  const { data: spaStaysPage } = useGetSpaStaysPage(property?.id, language);
  const { data: tablesPage } = useGetTablesPage(property?.id, language);

  const golfPage = useSelector(entitySelectors.getGolfPage) as ReduxPageType | undefined;
  const products = useSelector(entitySelectors.getAllProducts) as Product[];
  const productsTileImageUrl = products?.[0]?.imageGallery?.length
    ? products[0].imageGallery[0]?.url
    : products?.[0]?.image?.url;

  const categories = useMemo((): CategoryItem[] => {
    const items: CategoryItem[] = [];

    if (isRoomsEnabled && hasItems(roomsPage)) {
      items.push({
        id: 'rooms',
        label: labels?.rooms || t('flynav.rooms'),
        imageUrl: tileImage(roomsPage?.heroBackgroundImage, roomsPage?.fallbackHeroBackgroundImage),
      });
    }

    if (isRoomsEnabled && hasItems(ratesPage)) {
      items.push({
        id: 'rates',
        label: labels?.rates || t('flynav.rates'),
        imageUrl: tileImage(ratesPage?.heroBackgroundImage, ratesPage?.fallbackHeroBackgroundImage),
      });
    }

    if (property?.isTablesIntegrated && hasItems(tablesPage)) {
      items.push({
        id: 'tables',
        label: labels?.tables || t('flynav.dining'),
        imageUrl: tileImage(
          tablesPage?.heroBackgroundImage?.url,
          tablesPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isGolfVisible) {
      items.push({
        id: 'golf',
        label: labels?.golf || t('flynav.golf'),
        imageUrl: tileImage(golfPage?.heroBackgroundImage?.url, GOLF_DEFAULT_COURSE_IMAGE_URL),
      });
    }

    if (isSpaStaysVisible && hasItems(spaStaysPage)) {
      items.push({
        id: 'spa-stays',
        label: labels?.spastays || t('flynav.spaStays'),
        imageUrl: tileImage(
          spaStaysPage?.heroBackgroundImage?.url,
          spaStaysPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isFeaturedSpaDaysVisible && hasItems(spaDaysPage)) {
      items.push({
        id: 'spa-days',
        label: labels?.spadays || t('flynav.spaDays'),
        imageUrl: tileImage(
          spaDaysPage?.heroBackgroundImage?.url,
          spaDaysPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isFeaturedSpaTreatmentsVisible && hasItems(spaTreatmentsPage)) {
      items.push({
        id: 'spa-treatments',
        label: labels?.treatments || t('flynav.treatments'),
        imageUrl: tileImage(
          spaTreatmentsPage?.heroBackgroundImage?.url,
          spaTreatmentsPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isFacilitiesVisible && hasItems(spaFacilitiesPage)) {
      items.push({
        id: 'facilities',
        label: labels?.facilities || t('app.facilities'),
        imageUrl: tileImage(
          spaFacilitiesPage?.heroBackgroundImage?.url,
          spaFacilitiesPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isClassesVisible && hasItems(spaClassesPage)) {
      items.push({
        id: 'spa-classes',
        label: labels?.classes || t('flynav.classes'),
        imageUrl: tileImage(
          spaClassesPage?.heroBackgroundImage?.url,
          spaClassesPage?.fallbackHeroBackgroundImageUrl
        ),
      });
    }

    if (isRetailVisible && (!retailListLoaded || products?.length > 0)) {
      items.push({
        id: 'products',
        label: labels?.retail || t('flynav.shop'),
        imageUrl: productsTileImageUrl,
        imageOverlay: true,
      });
    }

    return items.map((item) => ({ ...item, href: categoryHref(item.id) }));
  }, [
    isRoomsEnabled,
    isGolfVisible,
    isSpaStaysVisible,
    isFeaturedSpaDaysVisible,
    isFeaturedSpaTreatmentsVisible,
    isClassesVisible,
    isFacilitiesVisible,
    isRetailVisible,
    retailListLoaded,
    products,
    productsTileImageUrl,
    property?.isTablesIntegrated,
    labels,
    t,
    roomsPage,
    ratesPage,
    spaDaysPage,
    spaTreatmentsPage,
    spaClassesPage,
    spaFacilitiesPage,
    spaStaysPage,
    tablesPage,
    golfPage,
  ]);

  const selectCategory = useCallback(
    (id: string) => {
      const route = categoryRoute(id);
      if (route) {
        router.push(route);
      }
    },
    [router]
  );

  return { categories, selectCategory };
}
